import { OnMount } from '@monaco-editor/react';
import debounce from 'lodash/debounce';
import type * as monacoT from 'monaco-editor';

const LINE_HEIGHT = 18;
const GUTTER = 10;

const editorFoldingLevel =
  (level: number, maxHeight?: number): OnMount =>
  (editor) => {
    const editorFolding = `editor.foldLevel${level}`;
    setTimeout(() => {
      if (!maxHeight || editor.getContentHeight() > maxHeight) {
        editor.getAction(editorFolding)?.run();
      }
    }, 50);
  };

export const editorDefaultFoldingLevel =
  (level: number, maxHeight?: number): OnMount =>
  (editor, monaco) => {
    editorFoldingLevel(level, maxHeight)(editor, monaco);
    editor.onDidChangeModelContent(() => editorFoldingLevel(level, maxHeight)(editor, monaco));
  };

export const editorAutoHeight =
  (maxHeight: number, minHeight = 0): OnMount =>
  (editor) => {
    const el = editor.getDomNode() as HTMLDivElement;
    const codeContainer = el.getElementsByClassName('view-lines')[0] as HTMLDivElement;
    let prevLineCount = 0;
    editor.onDidChangeModelDecorations(() => {
      // wait until dom rendered
      setTimeout(() => {
        const isUnFolded = codeContainer?.childElementCount > prevLineCount;
        const height = isUnFolded
          ? codeContainer?.offsetHeight // unfold
          : codeContainer.childElementCount * LINE_HEIGHT + GUTTER; // fold
        prevLineCount = codeContainer.childElementCount;
        if (height > maxHeight) {
          el.style.height = `${maxHeight}px`;
          el.style.minHeight = `${maxHeight}px`;
        } else {
          el.style.height = `${Math.max(minHeight, height)}px`;
          el.style.minHeight = `${Math.max(minHeight, height)}px`;
        }
        editor.layout();
      }, 0);
    });
  };

type MonacoValidationState = {
  hasErrors: boolean;
  errorMessage: string | null;
  firstError?: monacoT.editor.IMarker;
  markers: monacoT.editor.IMarker[];
};

type SubscribeValidationOptions = {
  includeWarnings?: boolean;
  debounceMs?: number;
};

export const subscribeToMonacoValidation =
  (onState: (state: MonacoValidationState) => void, opts: SubscribeValidationOptions = {}) =>
  (editor: monacoT.editor.IStandaloneCodeEditor, monaco: typeof monacoT) => {
    const { includeWarnings = false, debounceMs = 80 } = opts;

    const getMarkers = () => {
      const model = editor.getModel();
      if (!model) {
        return [];
      }

      const markers = monaco.editor.getModelMarkers({
        resource: model.uri,
      });

      const severities = includeWarnings
        ? [monaco.MarkerSeverity.Error, monaco.MarkerSeverity.Warning]
        : [monaco.MarkerSeverity.Error];

      return markers
        .filter((m) => severities.includes(m.severity))
        .sort((a, b) => a.startLineNumber - b.startLineNumber || a.startColumn - b.startColumn);
    };

    const emit = () => {
      const markers = getMarkers();
      const firstError = markers[0];

      const errorMessage = firstError ? firstError.message : null;

      onState({
        hasErrors: markers.some((m) => m.severity === monaco.MarkerSeverity.Error),
        errorMessage,
        firstError,
        markers,
      });
    };

    const debouncedEmit = debounce(emit, debounceMs);

    debouncedEmit();

    const decorationsSub = editor.onDidChangeModelDecorations(() => {
      debouncedEmit();
    });

    const modelSub = editor.onDidChangeModel(() => {
      debouncedEmit();
    });

    const dispose = () => {
      debouncedEmit.cancel();
      decorationsSub.dispose();
      modelSub.dispose();
    };

    editor.onDidDispose(dispose);

    return dispose;
  };

export const scrollToFirstDiff = (editor: monacoT.editor.IStandaloneDiffEditor) => {
  const dispose = editor.onDidUpdateDiff(() => {
    const changes = editor.getLineChanges();
    if (!changes || changes.length === 0) {
      return;
    }

    const first = changes[0];
    editor.getModifiedEditor().revealLineInCenter(first.modifiedStartLineNumber);
  });

  return () => {
    dispose.dispose();
  };
};
